import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CustomModal, CustomButton } from '../components';
import { authAPI } from '../services/api';
import './ProjectChatPage.css';

const ProjectChatPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  
  // Modals
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Create Project Form
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  
  // Invite Members Form
  const [inviteEmails, setInviteEmails] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  
  const messagesEndRef = useRef(null);
  const authCheckedRef = useRef(false);

  // Mock projects data - replace with API calls later
  const mockProjects = [
    {
      id: 1,
      name: 'Family History Project',
      description: 'Preserving family memories and stories',
      members: [
        { id: 1, name: 'John Doe', email: 'john@example.com', initials: 'JD' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com', initials: 'JS' },
        { id: 3, name: 'Bob Johnson', email: 'bob@example.com', initials: 'BJ' }
      ],
      createdBy: 'John Doe',
      createdAt: '2024-01-15'
    },
    {
      id: 2,
      name: 'Grandma\'s Recipes',
      description: 'Documenting and sharing family recipes',
      members: [
        { id: 1, name: 'John Doe', email: 'john@example.com', initials: 'JD' },
        { id: 4, name: 'Mary Williams', email: 'mary@example.com', initials: 'MW' }
      ],
      createdBy: 'John Doe',
      createdAt: '2024-01-20'
    }
  ];

  // Mock messages data
  const mockMessages = {
    1: [
      {
        id: 1,
        userId: 1,
        userName: 'John Doe',
        userInitials: 'JD',
        content: 'Hey everyone! I found some old photos we should add to this project.',
        time: '10:30 AM',
        type: 'user'
      },
      {
        id: 2,
        userId: 2,
        userName: 'Jane Smith',
        userInitials: 'JS',
        content: 'That\'s great! Can you share them?',
        time: '10:32 AM',
        type: 'user'
      },
      {
        id: 3,
        userId: 3,
        userName: 'Bob Johnson',
        userInitials: 'BJ',
        content: 'I have some documents too. Should I upload them here?',
        time: '10:35 AM',
        type: 'user'
      }
    ],
    2: [
      {
        id: 1,
        userId: 1,
        userName: 'John Doe',
        userInitials: 'JD',
        content: 'Let\'s start documenting Grandma\'s recipes!',
        time: '2:15 PM',
        type: 'user'
      },
      {
        id: 2,
        userId: 4,
        userName: 'Mary Williams',
        userInitials: 'MW',
        content: 'I have her famous apple pie recipe!',
        time: '2:20 PM',
        type: 'user'
      }
    ]
  };

  useEffect(() => {
    if (authCheckedRef.current) return;
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      authCheckedRef.current = true;
      toast.info('Please sign in to access Project Chat');
      navigate('/signin');
      return;
    }
    
    authCheckedRef.current = true;
    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    // Load mock projects
    setProjects(mockProjects);
    setLoadingData(false);
  }, []);

  useEffect(() => {
    if (selectedProject) {
      // Load messages for selected project
      setMessages(mockMessages[selectedProject.id] || []);
    }
  }, [selectedProject]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchUserData = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      toast.error('Failed to load user data');
      navigate('/signin');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.firstName || '';
    const lastName = user.last_name || user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    setCreatingProject(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newProject = {
      id: projects.length + 1,
      name: projectName,
      description: projectDescription,
      members: [
        { 
          id: user?.id || 1, 
          name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'You', 
          email: user?.email || 'you@example.com', 
          initials: getUserInitials() 
        }
      ],
      createdBy: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'You',
      createdAt: new Date().toISOString().split('T')[0]
    };

    setProjects([...projects, newProject]);
    setSelectedProject(newProject);
    setProjectName('');
    setProjectDescription('');
    setShowCreateProjectModal(false);
    setCreatingProject(false);
    toast.success('Project created successfully!');
  };

  const handleSendInvites = async (e) => {
    e.preventDefault();
    if (!inviteEmails.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    setSendingInvites(true);
    
    // Parse emails (comma or newline separated)
    const emails = inviteEmails
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      setSendingInvites(false);
      return;
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Add mock members to project
    if (selectedProject) {
      const newMembers = emails.map((email, index) => ({
        id: selectedProject.members.length + index + 1,
        name: email.split('@')[0],
        email: email,
        initials: email.substring(0, 2).toUpperCase()
      }));

      const updatedProject = {
        ...selectedProject,
        members: [...selectedProject.members, ...newMembers]
      };

      setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
      setSelectedProject(updatedProject);
    }

    setInviteEmails('');
    setShowInviteModal(false);
    setSendingInvites(false);
    toast.success(`Invitations sent to ${emails.length} member(s)!`);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || !selectedProject) return;

    const newMessage = {
      id: messages.length + 1,
      userId: user?.id || 1,
      userName: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'You',
      userInitials: getUserInitials(),
      content: inputValue,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      type: 'user'
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsTyping(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_session_project_id');
    localStorage.removeItem('chat_messages');
    navigate('/signin');
    toast.info('Logged out successfully');
  };

  if (loadingData) {
    return (
      <div className="project-chat-page">
        <div className="loading-spinner-wrapper">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="project-chat-page">
      {/* Sidebar */}
      <aside className="project-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src={process.env.PUBLIC_URL + '/Sayagain.png'} alt="SayAgain" className="sidebar-logo-img" />
          </Link>
        </div>

        <div className="sidebar-actions">
          <button 
            className="create-project-btn" 
            onClick={() => setShowCreateProjectModal(true)}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            New Project
          </button>
        </div>

        <div className="projects-list">
          <div className="projects-list-header">Projects</div>
          {projects.length === 0 ? (
            <div className="empty-projects">
              <p>No projects yet</p>
              <p className="empty-projects-hint">Create a project to get started</p>
            </div>
          ) : (
            projects.map(project => (
              <div
                key={project.id}
                className={`project-item ${selectedProject?.id === project.id ? 'active' : ''}`}
                onClick={() => setSelectedProject(project)}
              >
                <div className="project-item-icon">
                  {project.name.charAt(0).toUpperCase()}
                </div>
                <div className="project-item-content">
                  <div className="project-item-name">{project.name}</div>
                  <div className="project-item-meta">
                    {project.members.length} member{project.members.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">{getUserInitials()}</div>
            <div className="user-info">
              <div className="user-name">
                {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'User'}
              </div>
              <div className="user-email">{user?.email || 'user@example.com'}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="project-chat-main">
        {!selectedProject ? (
          <div className="no-project-selected">
            <div className="no-project-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3>Select a project to start chatting</h3>
            <p>Choose a project from the sidebar or create a new one</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <header className="project-chat-header">
              <div className="project-info">
                <div className="project-header-icon">
                  {selectedProject.name.charAt(0).toUpperCase()}
                </div>
                <div className="project-header-details">
                  <h2 className="project-header-name">{selectedProject.name}</h2>
                  <div className="project-header-meta">
                    <span className="project-members-count">
                      {selectedProject.members.length} member{selectedProject.members.length !== 1 ? 's' : ''}
                    </span>
                    {selectedProject.description && (
                      <span className="project-description">• {selectedProject.description}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="project-header-actions">
                <button 
                  className="invite-members-btn" 
                  onClick={() => setShowInviteModal(true)}
                  title="Invite Members"
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Invite Members
                </button>
                <button 
                  className="project-settings-btn" 
                  title="Project Settings"
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>
            </header>

            {/* Members List */}
            <div className="project-members-bar">
              <div className="members-list">
                {selectedProject.members.map(member => (
                  <div key={member.id} className="member-avatar" title={member.name}>
                    <div className="member-avatar-circle">{member.initials}</div>
                    <span className="member-name">{member.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Chat Messages */}
            <div className={`project-chat-messages ${messages.length === 0 && !isTyping ? 'empty' : ''}`}>
              {messages.length === 0 && !isTyping ? (
                <div className="chat-empty-state">
                  <div className="empty-state-icon">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">Start the conversation</h3>
                  <p className="empty-state-description">
                    Begin chatting with your team about {selectedProject.name}
                  </p>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div key={message.id} className={`project-message ${message.userId === (user?.id || 1) ? 'own' : ''}`}>
                      <div className="message-avatar">
                        {message.userInitials}
                      </div>
                      <div className="message-content-wrapper">
                        <div className="message-header">
                          <span className="message-author">{message.userName}</span>
                          <span className="message-time">{message.time}</span>
                        </div>
                        <div className="message-body">
                          {message.content}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isTyping && (
                    <div className="project-message typing">
                      <div className="message-avatar">
                        {getUserInitials()}
                      </div>
                      <div className="message-content-wrapper">
                        <div className="typing-indicator">
                          <Spinner animation="grow" size="sm" />
                          <Spinner animation="grow" size="sm" />
                          <Spinner animation="grow" size="sm" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Chat Input */}
            <div className="project-chat-input-area">
              <Form onSubmit={handleSendMessage} className="project-chat-input-wrapper">
                <Form.Control
                  type="text"
                  className="project-chat-input"
                  placeholder={`Message ${selectedProject.name}...`}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                <button type="submit" className="project-send-btn" disabled={!inputValue.trim()}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </Form>
            </div>
          </>
        )}
      </main>

      {/* Create Project Modal */}
      <CustomModal
        show={showCreateProjectModal}
        onHide={() => {
          setShowCreateProjectModal(false);
          setProjectName('');
          setProjectDescription('');
        }}
        title="Create New Project"
        subtitle="Start a new project to collaborate with your team"
        size="md"
      >
        <Form onSubmit={handleCreateProject}>
          <Form.Group className="mb-3">
            <Form.Label>Project Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Family History Project"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="What is this project about?"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
            />
          </Form.Group>
          <div className="modal-actions">
            <CustomButton
              variant="secondary"
              onClick={() => {
                setShowCreateProjectModal(false);
                setProjectName('');
                setProjectDescription('');
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="primary"
              disabled={creatingProject || !projectName.trim()}
            >
              {creatingProject ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </CustomButton>
          </div>
        </Form>
      </CustomModal>

      {/* Invite Members Modal */}
      <CustomModal
        show={showInviteModal}
        onHide={() => {
          setShowInviteModal(false);
          setInviteEmails('');
        }}
        title="Invite Members"
        subtitle="Add team members to collaborate on this project"
        size="md"
      >
        <Form onSubmit={handleSendInvites}>
          <Form.Group className="mb-3">
            <Form.Label>Email Addresses</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter email addresses separated by commas or new lines&#10;e.g., john@example.com, jane@example.com"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              required
            />
            <Form.Text className="text-muted">
              You can enter multiple email addresses separated by commas or new lines
            </Form.Text>
          </Form.Group>
          <div className="modal-actions">
            <CustomButton
              variant="secondary"
              onClick={() => {
                setShowInviteModal(false);
                setInviteEmails('');
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="primary"
              disabled={sendingInvites || !inviteEmails.trim()}
            >
              {sendingInvites ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                'Send Invitations'
              )}
            </CustomButton>
          </div>
        </Form>
      </CustomModal>
    </div>
  );
};

export default ProjectChatPage;
